"use client";

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
// Desktop Dialog components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
// Mobile Sheet components
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useScreenDetector } from "@/hooks/use-screen-detector";
import { DesktopAppointmentDetail } from "./components/desktop-appointment-detail";
import { MobileAppointmentDetail } from "./components/mobile-appointment-detail";

interface AppointmentDetailDialogProps {
  appointmentId: string | null;
  open: boolean;
  onClose: () => void;
}

export function AppointmentDetailDialog({
  appointmentId,
  open,
  onClose,
}: AppointmentDetailDialogProps) {
  const { t } = useTranslation("appointments");
  const { isMobile } = useScreenDetector();

  // Early return if dialog should not be shown or no appointment ID
  if (!open || !appointmentId) {
    return isMobile ? (
      <Sheet open={false}>
        <SheetContent side="bottom" className="h-[90vh]" />
      </Sheet>
    ) : (
      <Dialog open={false}>
        <DialogContent />
      </Dialog>
    );
  }

  // Mobile Sheet
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent side="bottom" className="h-[85vh] flex flex-col p-0 rounded-t-2xl" hideClose>
          <div className="flex-shrink-0 border-b shadow-sm px-4 py-1">
            <div className="flex items-center justify-between h-11">
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                className="text-blue-600 font-medium -ml-2 px-2 h-8"
              >
                {t("common:actions.cancel")}
              </Button>
              <SheetTitle className="text-base font-semibold">{t("details.title")}</SheetTitle>
              <div className="w-16" /> {/* Spacer for center alignment */}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <MobileAppointmentDetail appointmentId={appointmentId} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop Dialog
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-y-auto" hideClose>
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <DialogTitle>{t("details.title")}</DialogTitle>
            <DialogDescription>{t("details.description")}</DialogDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} className="ml-4">
            {t("common:actions.close")}
          </Button>
        </DialogHeader>
        <DesktopAppointmentDetail appointmentId={appointmentId} />
      </DialogContent>
    </Dialog>
  );
}
